'use client'

import { useState } from 'react'

import { Button, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Textarea } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import type { HomeConnectRequestDetail } from './request-summary'
import { OptionRow } from './option-row'
import { HomeConnectRequestSummary } from './request-summary'

/**
 * The reason travels to the school verbatim, in both the feed card and an email, so the presets are
 * worded as something a teacher can act on rather than as a bare error code.
 */
const PRESET_REASONS = ['Murid ini bukan anak saya', 'Saya tidak kenal guru atau sekolah ini'] as const

const OTHER = 'Lainnya'

interface RejectStepProps {
  detail: HomeConnectRequestDetail
  isSubmitting: boolean
  onReject: (reason: string) => void
  onBack: () => void
}

export function RejectStep({ detail, isSubmitting, onReject, onBack }: RejectStepProps) {
  const [choice, setChoice] = useState<string>(PRESET_REASONS[0])
  const [other, setOther] = useState('')

  const isOther = choice === OTHER
  const reason = isOther ? other.trim() : choice
  const canSubmit = reason.length > 0 && !isSubmitting

  return (
    <>
      <DialogHeader>
        <DialogTitle>Tolak Permohonan Sambung Sekolah-Rumah</DialogTitle>
        <DialogDescription>Tolak permohonan guru ini untuk tersambung dengan akun anakmu</DialogDescription>
      </DialogHeader>

      <HomeConnectRequestSummary detail={detail} />

      <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>Alasan penolakan?</p>

      <div role='radiogroup' aria-label='Alasan penolakan' className='srd:flex srd:flex-col srd:gap-y-1'>
        {PRESET_REASONS.map((preset) => (
          <OptionRow key={preset} selected={choice === preset} onSelect={() => setChoice(preset)}>
            {preset}
          </OptionRow>
        ))}
        <OptionRow selected={isOther} onSelect={() => setChoice(OTHER)}>
          {OTHER}
        </OptionRow>
      </div>

      {isOther && (
        <Textarea
          value={other}
          rows={3}
          maxLength={500}
          placeholder='Tulis alasanmu'
          aria-label='Alasan lainnya'
          onChange={(event) => setOther(event.target.value)}
        />
      )}

      <DialogFooter className='srd:flex-col srd:gap-y-2'>
        <Button className='srd:w-full' loading={isSubmitting} disabled={!canSubmit} onClick={() => onReject(reason)}>
          Tolak permintaan
        </Button>
        <Button variant='ghost' className='srd:w-full' disabled={isSubmitting} onClick={onBack}>
          Kembali ke permohonan
        </Button>
      </DialogFooter>
    </>
  )
}
